import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
  ReactNode,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { CheckCircle, AlertCircle, Info, X, Copy, Check } from "lucide-react";
import "./Notification.scss";

type NotificationVariant = "success" | "error" | "info";

export interface NotificationData {
  message: string;
  variant: NotificationVariant;
  duration?: number;
  copyText?: string;
}

interface NotificationContextType {
  showNotification: (data: NotificationData) => void;
  hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const ICONS: Record<NotificationVariant, ReactNode> = {
  success: <CheckCircle size={24} />,
  error: <AlertCircle size={24} />,
  info: <Info size={24} />,
};

const copy = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [item, setItem] = useState<NotificationData | null>(null);
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setReady(true);
  }, []);

  const hide = useCallback(() => {
    setOpen(false);
    setCopied(false);
  }, []);

  const show = useCallback(({ message, variant, duration = 3000, copyText }: NotificationData) => {
    if (timer.current) clearTimeout(timer.current);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    setItem({ message, variant, copyText });
    setCopied(false);
    setOpen(true);

    if (duration > 0) {
      timer.current = setTimeout(() => {
        setOpen(false);
        timer.current = null;
      }, duration);
    }
  }, []);

  const onCopy = useCallback(async () => {
    if (!item?.copyText) return;
    const ok = await copy(item.copyText);
    if (!ok) return;
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1500);
  }, [item?.copyText]);

  return (
    <NotificationContext.Provider value={{ showNotification: show, hideNotification: hide }}>
      {children}
      {ready &&
        open &&
        item &&
        createPortal(
          <div className={`notification${item.copyText ? " notification--detail" : ""}`}>
            <div className={`notification-box notification-box--${item.variant}`}>
              <button
                type="button"
                className="notification-close"
                onClick={hide}
                aria-label="Закрыть"
              >
                <X size={16} />
              </button>
              <div className="notification-icon">{ICONS[item.variant]}</div>
              <div className="notification-body">
                <span className="notification-message">{item.message}</span>
                {item.copyText ? (
                  <button type="button" className="notification-copy" onClick={onCopy}>
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    <span>{copied ? "Скопировано" : "Скопировать ошибку"}</span>
                  </button>
                ) : null}
              </div>
            </div>
          </div>,
          document.body
        )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return ctx;
};
